import { fetchRestaurantOrdersList } from './restaurantIncomingOrderActions';

const DEFAULT_POLL_INTERVAL_MS = 6000;

const subscribers = new Map();
let pollTimerId = null;
let inflightPromise = null;
let lastOrdersSnapshot = null;
let lastFingerprint = '';
let trackedHotelId = null;
let visibilityListenerAttached = false;

function isDocumentVisible() {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

function buildOrdersFingerprint(orders = []) {
  return (Array.isArray(orders) ? orders : [])
    .map((order) => {
      const itemCount = Array.isArray(order?.items) ? order.items.length : 0;
      return [
        Number(order?.id || 0),
        String(order?.status || ''),
        String(order?.updated_at || order?.created_at || ''),
        itemCount,
        Number(order?.total_amount || 0),
      ].join(':');
    })
    .sort()
    .join('|');
}

function notifySubscribers(orders, meta = {}) {
  subscribers.forEach((callback) => {
    try {
      callback(orders, meta);
    } catch {
      // Subscriber errors should not break polling.
    }
  });
}

function handleVisibilityChange() {
  if (!isDocumentVisible()) return;
  void runRestaurantOrdersPoll({ force: true });
}

function ensureVisibilityListener() {
  if (visibilityListenerAttached || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = true;
}

function removeVisibilityListenerIfIdle() {
  if (subscribers.size > 0 || !visibilityListenerAttached || typeof document === 'undefined') return;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = false;
}

function stopPollingIfIdle() {
  if (subscribers.size > 0) return;
  if (pollTimerId) {
    window.clearInterval(pollTimerId);
    pollTimerId = null;
  }
  removeVisibilityListenerIfIdle();
}

function startPollingIfNeeded() {
  if (pollTimerId || subscribers.size === 0) return;
  ensureVisibilityListener();
  void runRestaurantOrdersPoll({ force: true });
  pollTimerId = window.setInterval(() => {
    void runRestaurantOrdersPoll();
  }, DEFAULT_POLL_INTERVAL_MS);
}

function resetPollCache(hotelId) {
  trackedHotelId = hotelId ?? null;
  lastFingerprint = '';
  lastOrdersSnapshot = null;
}

export async function runRestaurantOrdersPoll({ force = false, hotelId = trackedHotelId } = {}) {
  if (!isDocumentVisible()) return lastOrdersSnapshot;
  if (subscribers.size === 0) return lastOrdersSnapshot;
  if (inflightPromise) return inflightPromise;

  inflightPromise = fetchRestaurantOrdersList()
    .then((orders) => {
      const normalizedOrders = Array.isArray(orders) ? orders : [];
      const fingerprint = buildOrdersFingerprint(normalizedOrders);
      const changed = force || fingerprint !== lastFingerprint;
      lastFingerprint = fingerprint;
      lastOrdersSnapshot = normalizedOrders;
      notifySubscribers(normalizedOrders, { changed, hotelId });
      return normalizedOrders;
    })
    .catch(() => lastOrdersSnapshot)
    .finally(() => {
      inflightPromise = null;
    });

  return inflightPromise;
}

export function subscribeRestaurantOrdersPoll(callback, {
  hotelId = null,
  immediate = true,
} = {}) {
  if (hotelId != null && hotelId !== trackedHotelId) {
    resetPollCache(hotelId);
  }

  const subscriberId = Symbol('restaurant-orders-poll');
  subscribers.set(subscriberId, callback);
  startPollingIfNeeded();

  if (immediate && lastOrdersSnapshot) {
    callback(lastOrdersSnapshot, { changed: false, cached: true, hotelId });
  } else if (immediate) {
    void runRestaurantOrdersPoll({ force: true, hotelId });
  }

  return () => {
    subscribers.delete(subscriberId);
    stopPollingIfIdle();
  };
}

export function getCachedRestaurantOrdersSnapshot() {
  return lastOrdersSnapshot;
}
